import {
  AvatarSizeVariant,
  Badge,
  ManageableItemsList,
  TTableRowAction,
  UserAvatar,
} from '@payaca/ambrosia';
import { FunctionComponent, useCallback, useState, type JSX } from 'react';

import useDeactivateUser from '@/api/mutations/account/useDeactivateUser';
import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyAccountTeams from '@/api/queries/me/useGetMyAccountTeams';
import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { AddUserModal } from '@/ui/components';
import ConfirmDeleteUserModal from '@/ui/components/confirmDeleteUserModal/ConfirmDeleteUserModal';
import ConfirmMakePrimaryUserModal from '@/ui/components/confirmMakePrimaryUserModal/ConfirmMakePrimaryUserModal';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import SettingsLayout from '@/ui/components/companySettings/SettingsLayout';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import {
  CustomOvoSystemRoles,
  DefaultSystemRoles,
} from '@payaca/permissions/default-role.config';
import { User, UserStatus } from '@payaca/types/userTypes';
import { useHistory } from 'react-router';
import ConfirmResendInviteModal from './ConfirmResendInviteModal';
import EditUserSortOrderModal from './EditUserSortOrderModal';
import './ListedUsersPage.sass';
import ManageMembersModal from './ManageMembersModal';
import NewTeamModal from './NewTeamModal';
import TeamsList from './TeamsList';
import UserTeamsCell from './UserTeamsCell';
import { AccountUser, useUsersTableFilters } from './useUsersTableFilters';

const statusBadgeColour = (status: string) => {
  switch (status) {
    case UserStatus.ACTIVE:
      return 'green';
    case UserStatus.INVITE_EXPIRED:
      return 'red';
    default:
      return 'yellow';
  }
};

/** The Users & teams settings section, rendered in the Company Settings pane. */
export const UsersSettings: FunctionComponent = (): JSX.Element => {
  const history = useHistory();

  const [makePrimaryUserId, setMakePrimaryUserId] = useState<User['id']>();
  const [deleteUserPublicId, setDeleteUserPublicId] = useState<string>();
  const [resendInviteUser, setResendInviteUser] = useState<{
    publicId: string;
    fullName: string;
  }>();
  const [editUserId, setEditUserId] = useState<User['id']>();
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);

  const { mutate: deactivateUser, isPending: isDeactivatingUser } =
    useDeactivateUser();

  const {
    data: activeUsers,
    refetch: refetchAccountUsers,
    isLoading,
  } = useGetMyAccountUsersExcludedDeactivated();
  const { data: me, refetch: refetchMe } = useGetMe();
  const { data: accountTeams } = useGetMyAccountTeams();

  const {
    searchTerm,
    onSearchTermChange,
    applySearch,
    selectedTeamId,
    onSelectTeam,
    teamListItems,
    anyUserHasTeam,
    allUsersCount,
    roleFilter,
    onRoleFilterChange,
    roleOptions,
    statusFilter,
    onStatusFilterChange,
    statusOptions,
    sortBy,
    sortDirection,
    onSort,
    pageNumber,
    onPageChange,
    pageSize,
    filteredUsers,
    pagedUsers,
    anyActiveUserHasCostPerHour,
    anyActiveUserHasSortOrder,
  } = useUsersTableFilters(activeUsers, accountTeams ?? []);

  const selectedTeam = teamListItems.find((team) => team.id === selectedTeamId);
  const paneHeading = selectedTeam ? selectedTeam.name : 'All users';

  const getItemActions = useCallback(
    (item: AccountUser) => {
      const actions: TTableRowAction<AccountUser>[] = [];

      if (!me?.permissions.includes(AccountsPermissions.UPDATE_ACCOUNT))
        return [];

      actions.push({
        label: 'Edit sort order',
        onClick: (item) => {
          setEditUserId(+item.id);
        },
      });

      if (
        !item.isPrimaryUser &&
        ((item.systemRole as DefaultSystemRoles) ===
          DefaultSystemRoles.SUPER_ADMIN ||
          (item.systemRole as CustomOvoSystemRoles) ===
            CustomOvoSystemRoles.OVO_FRANCHISOR_ADMIN) &&
        me?.user.isPrimaryUser
      ) {
        actions.push({
          label: 'Make primary user',
          onClick: (item) => {
            setMakePrimaryUserId(+item.id);
          },
        });
      }

      if (item.hasOutstandingInvite) {
        actions.push({
          label: 'Resend invite',
          onClick: (item) => {
            setResendInviteUser({
              publicId: item.publicId,
              fullName: item.fullName,
            });
          },
        });
      }

      if (!item.isPrimaryUser && item.id !== me?.user.id) {
        actions.push({
          label: 'Delete',
          onClick: (item) => {
            setDeleteUserPublicId(item.publicId);
          },
        });
      }

      return actions;
    },
    [me]
  );

  return (
    <>
      <div className="flex flex-col gap-4 p-4 md:flex-row">
        <TeamsList
          className="w-full shrink-0 overflow-y-auto rounded-lg border border-gray-200 md:sticky md:top-4 md:w-48 md:self-start lg:w-52 xl:w-56 2xl:w-64"
          allUsersCount={allUsersCount}
          teams={teamListItems}
          selectedTeamId={selectedTeamId}
          onSelectTeam={onSelectTeam}
          onNewTeam={() => setShowNewTeamModal(true)}
        />
        <ManageableItemsList className="min-w-0 flex-1 md:self-start">
          <ManageableItemsList.HeaderBar heading={paneHeading} />
          <ManageableItemsList.ActionBar>
            <ManageableItemsList.ActionBar.SearchInput
              value={searchTerm}
              onChange={onSearchTermChange}
              onChangeTimeout={applySearch}
              onBlur={() => applySearch(searchTerm)}
              placeholder="Search members"
              changeTimeoutMs={300}
            />
            <ManageableItemsList.ActionBar.BasicFilter
              name="Role"
              options={roleOptions}
              value={roleFilter}
              onChange={onRoleFilterChange}
            />
            <ManageableItemsList.ActionBar.BasicFilter
              name="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={onStatusFilterChange}
            />
            {selectedTeam && (
              <PermissionGuard
                renderIfHasPermissions={[
                  AccountsPermissions.MANAGE_TEAM_MEMBERS,
                ]}
              >
                <ManageableItemsList.ActionBar.GlobalAction
                  variant="primary"
                  tooltipContent="Add or remove members of this team"
                  onClick={() => setShowManageMembersModal(true)}
                >
                  Manage members
                </ManageableItemsList.ActionBar.GlobalAction>
              </PermissionGuard>
            )}
            {!selectedTeam && (
              <PermissionGuard
                renderIfHasPermissions={[AccountsPermissions.UPDATE_ACCOUNT]}
              >
                <ManageableItemsList.ActionBar.GlobalAction
                  tooltipContent="Invite a new user to join your account"
                  onClick={() => {
                    setShowAddUserModal(true);
                  }}
                >
                  Add user
                </ManageableItemsList.ActionBar.GlobalAction>
              </PermissionGuard>
            )}
          </ManageableItemsList.ActionBar>
          <ManageableItemsList.Table<AccountUser>
            isLoading={isLoading}
            items={pagedUsers}
            uniqueKey="id"
            itemActions={getItemActions}
            onClickRow={(item) => history.push(`/users/${item.id}`)}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            <ManageableItemsList.Table.Column
              header="Name"
              field="fullName"
              onSort={(_, direction) => onSort('fullName', direction)}
              render={(_, user: AccountUser) => {
                return (
                  <div className="flex items-center gap-4">
                    <UserAvatar
                      sizeVariant={AvatarSizeVariant.SM}
                      user={{
                        firstName: user.firstName,
                        lastName: user.lastName,
                        emailAddress: user.email,
                        userColour: user.colour,
                        imgSrc: user.avatarUrl,
                      }}
                      enableUserInfoTooltip={true}
                    />
                    {user.fullName}
                  </div>
                );
              }}
            />
            <ManageableItemsList.Table.Column
              header="Email"
              field="email"
              onSort={(_, direction) => onSort('email', direction)}
            />
            <ManageableItemsList.Table.Column
              header="Role"
              field="systemRole"
              onSort={(_, direction) => onSort('systemRole', direction)}
              render={(_, user: AccountUser) => {
                return (
                  <div>
                    <div>
                      {user.systemRole === DefaultSystemRoles.SUPER_ADMIN ? (
                        <strong>{user.systemRole}</strong>
                      ) : (
                        user.systemRole
                      )}
                    </div>
                    {user.isPrimaryUser && (
                      <div className="text-sm text-gray-600">Primary user</div>
                    )}
                  </div>
                );
              }}
            />
            {anyUserHasTeam && (
              <ManageableItemsList.Table.Column
                header="Teams"
                field="teams"
                render={(_, user: AccountUser) => (
                  <UserTeamsCell teams={user.teams.items} />
                )}
              />
            )}
            {anyActiveUserHasCostPerHour && (
              <ManageableItemsList.Table.Column
                header="Cost/hr"
                field="costPerHour"
                className="text-right"
                render={(_, user: AccountUser) => {
                  return user.costPerHour ? (
                    <CurrencyValue
                      value={user.costPerHour.value}
                      currency={user.costPerHour.currency}
                    />
                  ) : null;
                }}
              />
            )}
            <ManageableItemsList.Table.Column
              header="Status"
              field="status"
              onSort={(_, direction) => onSort('status', direction)}
              render={(_, user: AccountUser) => {
                return (
                  <Badge colour={statusBadgeColour(user.status)} variant="soft">
                    {user.status}
                  </Badge>
                );
              }}
            />
            {anyActiveUserHasSortOrder && (
              <ManageableItemsList.Table.Column
                header="Order"
                field="sortOrder"
                className="text-center"
              />
            )}
          </ManageableItemsList.Table>
          <ManageableItemsList.PaginationBar
            type="custom"
            pageSize={pageSize}
            currentPage={pageNumber}
            totalItems={filteredUsers.length}
            onPageChange={onPageChange}
          />
        </ManageableItemsList>
      </div>
      {makePrimaryUserId && (
        <ConfirmMakePrimaryUserModal
          userId={makePrimaryUserId}
          isOpen={!!makePrimaryUserId}
          onClose={() => {
            setMakePrimaryUserId(undefined);
          }}
          onMakePrimaryUserSuccess={() => {
            // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
            refetchAccountUsers();
            // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
            refetchMe();
            setMakePrimaryUserId(undefined);
          }}
        />
      )}
      <ConfirmDeleteUserModal
        isOpen={!!deleteUserPublicId}
        isBusy={isDeactivatingUser}
        onClose={() => {
          setDeleteUserPublicId(undefined);
        }}
        numSelected={1}
        onConfirmDelete={() => {
          if (!deleteUserPublicId) return;
          deactivateUser(
            { userId: deleteUserPublicId },
            { onSuccess: () => setDeleteUserPublicId(undefined) }
          );
        }}
      />
      <ConfirmResendInviteModal
        userPublicId={resendInviteUser?.publicId}
        userName={resendInviteUser?.fullName}
        isOpen={!!resendInviteUser}
        onClose={() => setResendInviteUser(undefined)}
      />
      <EditUserSortOrderModal
        userId={editUserId}
        isOpen={!!editUserId}
        onClose={() => setEditUserId(undefined)}
        onSuccess={() => {
          void refetchAccountUsers();
          setEditUserId(undefined);
        }}
      />
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onAddUserSuccess={() => {
          // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
          refetchAccountUsers();
          setShowAddUserModal(false);
        }}
      />
      <NewTeamModal
        isOpen={showNewTeamModal}
        onClose={() => setShowNewTeamModal(false)}
        onSuccess={(team) => {
          onSelectTeam(team.id);
          setShowNewTeamModal(false);
        }}
      />
      {selectedTeam && (
        <ManageMembersModal
          isOpen={showManageMembersModal}
          onClose={() => setShowManageMembersModal(false)}
          team={selectedTeam}
          users={activeUsers}
        />
      )}
    </>
  );
};

/**
 * Standalone Users page. Only routed while the account is over its user
 * seats — routing is locked to upgrade/users/personal then, so the section
 * cannot render through the Company Settings shell.
 */
const UsersPage: FunctionComponent = (): JSX.Element => (
  <AuthenticatedPageWrapper>
    <SettingsLayout>
      <UsersSettings />
    </SettingsLayout>
  </AuthenticatedPageWrapper>
);

export default UsersPage;
